import { Link } from "react-router-dom";
import { AGENT_LOGIN_URL, APP_HOME_URL, BROKER_LOGIN_URL, appUrl } from "../config";
import { useJourneyAuth } from "../auth/AuthContext";

export default function Sales() {
  const { session, role } = useJourneyAuth();
  const manualTo = session && role && role !== "admin" ? `/manuals/${role}` : "/manuals";

  return (
    <article className="home-page" data-tour="journey-home">
      <section className="home-hero" data-tour="journey-hero">
        <div className="home-hero__glow" aria-hidden="true" />
        <p className="home-kicker">DreamHomeJourney.online</p>
        <div className="home-hero__title" data-tour="journey-clouds">
          <figure className="home-hero__cloud home-hero__cloud--open">
            <img
              src="/images/hero-open-house.png"
              alt="An open house gathering on a front porch"
            />
          </figure>
          <h1 data-tour="journey-title">
            The Path Home
            <span>Held with Care</span>
          </h1>
          <figure className="home-hero__cloud home-hero__cloud--sold">
            <img
              src="/images/hero-sold-family.png"
              alt="An agent celebrating a sold home with a family"
            />
          </figure>
        </div>
        <p className="home-hero__porch" data-tour="journey-porch">
          A welcoming porch and support hub for REChecklist — the authoritative answer hub for
          buyers, sellers, agents, and brokers
        </p>
        <p className="home-hero__points">
          <span>Support that stays close</span>
          <span>Support based marketing</span>
          <span>Support focused on your success</span>
        </p>
        <div className="btn-row">
          <a className="btn" href={APP_HOME_URL}>
            Open REChecklist
          </a>
          <Link className="btn btn-soft" to="/support">
            Get support
          </Link>
          <Link className="btn btn-ghost" to={manualTo}>
            Open the manuals
          </Link>
        </div>
      </section>

      <section className="home-fold">
        <div className="home-panel home-panel--sales" data-tour="journey-desk">
          <p className="home-panel__eyebrow">The working desk</p>
          <h2>REChecklist is where the work lives</h2>
          <p>
            Name what matters. Keep it organized. Share it with the people who will carry it. Buyer
            and seller journeys never mix. Agents hold one client in one File Packet. Brokers
            supervise without turning the office into a race.
          </p>
          <ul className="home-points">
            <li>A calm buyer’s checklist that becomes a brief an agent can actually use</li>
            <li>A seller’s portfolio that tells the truth about the house that is there</li>
            <li>Messaging, files, and retention that belong to the transaction — not a noisy inbox</li>
          </ul>
          <div className="btn-row btn-row--left">
            <a className="btn" href={APP_HOME_URL}>
              Open REChecklist
            </a>
            <Link className="btn btn-ghost" to="/why-rechecklist">
              Why REChecklist
            </Link>
          </div>
        </div>
        <div className="home-panel home-panel--support" data-tour="journey-porch-card">
          <p className="home-panel__eyebrow">The front porch</p>
          <h2>This site is where we walk with you</h2>
          <p>
            Manuals behind a quiet sign-in. A Guided Tour that shows motion without a scavenger
            hunt. Support, community, vendors, and a human contact door — so you are never left
            holding a product without a companion.
          </p>
          <ul className="home-points">
            <li>Role books for buyer, seller, agent, and broker — topical, searchable, kind</li>
            <li>FAQ, contact, and events for San Diego County, with Southern California next</li>
            <li>A community board that stays professional, not a shouting floor</li>
          </ul>
          <div className="btn-row btn-row--left">
            <Link className="btn" to="/support">
              Visit support
            </Link>
            <Link className="btn btn-ghost" to="/contact">
              Write to us
            </Link>
          </div>
        </div>
      </section>

      <section className="home-roles">
        <h2>Who we hold the door for</h2>
        <p className="lead home-roles__lead">
          Four seats, four carpets. Choose the one that is yours — the others will still be there
          when you need them.
        </p>
        <div className="home-role-grid">
          <Link className="home-role" to="/benefits/buyers" data-tour="journey-role-buyers">
            <span className="home-role__who">Buyers</span>
            <strong>Turn a feeling about home into a brief</strong>
            <span>Location, light, lifestyle, and the notes only you would think to write.</span>
          </Link>
          <Link className="home-role" to="/benefits/sellers" data-tour="journey-role-sellers">
            <span className="home-role__who">Sellers</span>
            <strong>Describe the house that is there</strong>
            <span>Features, condition, photos — honesty that helps an agent represent you well.</span>
          </Link>
          <Link className="home-role" to="/benefits/agents" data-tour="journey-role-agents">
            <span className="home-role__who">Agents</span>
            <strong>Hold one client, clearly</strong>
            <span>Two messaging rooms that never mix, and a File Packet that stays together.</span>
          </Link>
          <Link className="home-role" to="/benefits/brokers" data-tour="journey-role-brokers">
            <span className="home-role__who">Brokers</span>
            <strong>Supervise without the scramble</strong>
            <span>Retention, audit, and a desk that respects the office’s calm.</span>
          </Link>
        </div>
      </section>

      <section className="home-support-strip">
        <h2>Support, close at hand</h2>
        <div className="home-support-grid">
          <Link to={manualTo} className="home-support-card" data-tour="journey-card-manuals">
            <h3>User manuals</h3>
            <p>Sign in as your role. Search any page or term. Start Here is written for first light.</p>
          </Link>
          <Link to="/support/tour" className="home-support-card" data-tour="journey-card-tour">
            <h3>Guided Tour</h3>
            <p>Watch the desk move — agent or broker — without hunting through menus.</p>
          </Link>
          <Link to="/faq" className="home-support-card">
            <h3>FAQ</h3>
            <p>Short answers for billing, roles, and how the two sites connect.</p>
          </Link>
          <Link to="/community" className="home-support-card">
            <h3>Community</h3>
            <p>A quiet board for practice, not performance. Events and calendar live next door.</p>
          </Link>
        </div>
      </section>

      <section className="home-look">
        <div className="home-panel home-look__film">
          <p className="home-panel__eyebrow">A moment at the desk</p>
          <h2>Watch the work, when you are ready</h2>
          <div className="home-film" role="region" aria-label="A moment at the desk">
            <p>REChecklist for Real Estate Professionals and the Dream Home Journey.</p>
            <p>A quiet walk of the live desk — kept for the office, not the public porch.</p>
          </div>
        </div>
        <div className="home-look__side">
          <article className="home-panel">
            <p className="home-panel__eyebrow">Voices from the porch</p>
            <h2>What offices may say</h2>
            <blockquote>
              “The desk stayed quiet enough that my agents actually finished files.”
            </blockquote>
            <p className="muted">Coastal office broker — placeholder until a real note arrives.</p>
            <Link to="/testimonials">Read the placeholders</Link>
          </article>
          <article className="home-panel">
            <p className="home-panel__eyebrow">Seats and care</p>
            <h2>Pricing, when you are ready</h2>
            <p>
              Agent Standard starts at $29/month. Office seats have their own tiers. Checkout is
              PayPal, then
              you return to the desk. Nothing here is a public trial.
            </p>
            <Link className="btn btn-ghost" to="/pricing">
              View pricing
            </Link>
          </article>
        </div>
      </section>

      <section className="home-cta">
        <div>
          <h2>Ready to Get Started?</h2>
          <p>
            Open the desk, read the book that belongs to your seat, or ask a question. The carpet
            is already down.
          </p>
        </div>
        <div className="btn-row btn-row--left">
          <a className="btn" href={APP_HOME_URL}>
            Open REChecklist
          </a>
          <a className="btn btn-ghost" href={appUrl("/login")}>
            Buyer sign-in
          </a>
          <a className="btn btn-ghost" href={AGENT_LOGIN_URL}>
            Agent desk
          </a>
          <a className="btn btn-ghost" href={BROKER_LOGIN_URL}>
            Broker desk
          </a>
        </div>
      </section>
    </article>
  );
}
